import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { EnedisAccessRightModel, EnedisTechnicalDataModel } from './enedis.models';
import { EnedisService } from './enedis.service';
import { GrdfTechnicalDataModel } from '../grdf/grdf.models';

/** This file is divided in 3 parts:
 * - The definition of the Store : defining data structure
 * - Constants that allow NgXs to trigger Actions
 * - Selectors & Actions : Functions allowed to interact with the store
 *
 */

// Store definition
export interface EnedisStateModel {
    enedisAccessRights: EnedisAccessRightModel[];
    currentAccessRight: EnedisAccessRightModel | undefined;
    currentTechnicalData: EnedisTechnicalDataModel | undefined;
    isLoading: boolean;
    forceReload: boolean;
}

//Constants

export class GetAllEnedisAccessRights {
    static readonly type = 'ENEDIS/ACCESSRIGHT/ALL';
}

export class RetryAccessRight {
    static readonly type = 'ENEDIS/ACCESSRIGHT/RETRY';
    constructor(public payload: { accessRightId: string }) {}
}

export class DeleteAccessRight {
    static readonly type = 'ENEDIS/ACCESSRIGHT/DELETE';
    constructor(public payload: { accessRightId: string }) {}
}

export class CreateAccessRight {
    static readonly type = 'ENEDIS/ACCESSRIGHT/CREATE';
    constructor(
        public payload: {
            accessRight: Partial<EnedisAccessRightModel>;
            fileProof?: File | null | undefined;
        },
    ) {}
}

export class UpdateFileProof {
    static readonly type = 'ENEDIS/FILEPROOF/UPDATE';
    constructor(public payload: { accessRightId: string; fileProof: File }) {}
}

export class UnsetCurrentAccessRightId {
    static readonly type = 'ENEDIS/ACCESSRIGHT/UNSET';
    constructor() {}
}

export class SetCurrentAccessRightId {
    static readonly type = 'ENEDIS/ACCESSRIGHT/SET';
    constructor(public payload: { id: string }) {}
}

export class GetTechnicalData {
    static readonly type = 'ENEDIS/TECHNICAL/GET';
    constructor(public payload: { accessRightId: string }) {}
}

export class UpdateEnedisForceReload {
    static readonly type = 'ENEDIS/FORCERELOAD';
    constructor(public payload: { forceReload: boolean }) {}
}
//Actions

@State<EnedisStateModel>({
    name: 'EnedisState',
    defaults: {
        enedisAccessRights: [],
        currentAccessRight: undefined,
        currentTechnicalData: undefined,
        isLoading: false,
        forceReload: false,
    },
})
@Injectable()
export class EnedisState {
    @Selector()
    public static enedisAccessRights(state: EnedisStateModel): EnedisAccessRightModel[] | null {
        return state.enedisAccessRights;
    }

    @Selector()
    public static forceReload(state: EnedisStateModel): boolean {
        return state.forceReload;
    }

    @Selector()
    public static currentAccessRight(state: EnedisStateModel): EnedisAccessRightModel | undefined {
        return state.currentAccessRight;
    }
    @Selector()
    public static currentAccessRightTechnicalData(
        state: EnedisStateModel,
    ): EnedisTechnicalDataModel | undefined {
        return state.currentTechnicalData;
    }

    @Selector()
    static isLoading(state: EnedisStateModel): boolean {
        return state.isLoading;
    }

    constructor(
        private readonly enedisService: EnedisService,
        private readonly store: Store,
    ) {}

    @Action(UpdateEnedisForceReload)
    public async updateForceReload(
        ctx: StateContext<EnedisStateModel>,
        action: UpdateEnedisForceReload,
    ): Promise<void> {
        const forceReload = action.payload.forceReload;
        const currentState = ctx.getState();
        ctx.patchState({
            ...currentState,
            forceReload: forceReload,
        });

        //Force reload all data
        if (forceReload && currentState.currentAccessRight?.id) {
            ctx.dispatch(new SetCurrentAccessRightId({ id: currentState.currentAccessRight.id }));
        }
    }

    @Action(SetCurrentAccessRightId)
    public async setCurrentAccessRightId(
        ctx: StateContext<EnedisStateModel>,
        action: SetCurrentAccessRightId,
    ): Promise<void> {
        const currentState = ctx.getState();
        //Reload just in case
        if (currentState.enedisAccessRights.length == 0) {
            ctx.dispatch(GetAllEnedisAccessRights);
        }
        if (action.payload.id) {
            const currentAccessRight = await this.enedisService.getEnedisAccessRight(
                action.payload.id,
            );

            let currentTechnicalData: EnedisTechnicalDataModel | undefined = undefined;

            try {
                currentTechnicalData = await this.enedisService.getTechnicalData(
                    action.payload.id,
                    currentState.forceReload,
                );
                if (currentTechnicalData?.dateDerniereModificationFormuleTarifaireAcheminement)
                    currentTechnicalData.dateDerniereModificationFormuleTarifaireAcheminement =
                        new Date(
                            currentTechnicalData.dateDerniereModificationFormuleTarifaireAcheminement,
                        );
            } catch (e) {
                console.log(e);
            }

            ctx.patchState({ ...currentState, currentAccessRight, currentTechnicalData });
        }
    }

    @Action(UnsetCurrentAccessRightId)
    public async unsetCurrentAccessRightId(
        ctx: StateContext<EnedisStateModel>,
        action: UnsetCurrentAccessRightId,
    ): Promise<void> {
        const currentState = ctx.getState();
        ctx.patchState({
            ...currentState,
            currentAccessRight: undefined,
            currentTechnicalData: undefined,
        });
    }

    @Action(GetAllEnedisAccessRights)
    public async getAllAccessRights(
        ctx: StateContext<EnedisStateModel>,
        action: GetAllEnedisAccessRights,
    ): Promise<void> {
        ctx.patchState({ isLoading: true });

        const enedisAccessRights = await this.enedisService.getEnedisAccessRights();

        const currentState = ctx.getState();
        currentState.enedisAccessRights = enedisAccessRights;
        currentState.isLoading = false;
        if (currentState.currentAccessRight) {
            currentState.currentAccessRight = enedisAccessRights.find(
                (ear) => ear.id === currentState.currentAccessRight?.id,
            );
        }
        ctx.patchState(currentState);
    }

    @Action(CreateAccessRight)
    public async createAccessRight(
        ctx: StateContext<EnedisStateModel>,
        action: CreateAccessRight,
    ): Promise<void> {
        try {
            const msg = await this.enedisService.createEnedisAccessRight(
                action.payload.accessRight,
                action.payload.fileProof,
            );
            ctx.dispatch(GetAllEnedisAccessRights);
        } catch (err: any) {
            console.log(err);
            throw new Error(err);
        }
    }

    @Action(UpdateFileProof)
    public async updateFileProof(
        ctx: StateContext<EnedisStateModel>,
        action: UpdateFileProof,
    ): Promise<void> {
        try {
            const msg = await this.enedisService.postNewFileProof(
                action.payload.accessRightId,
                action.payload.fileProof,
            );
            ctx.dispatch(GetAllEnedisAccessRights);
        } catch (err: any) {
            console.log(err);
            throw new Error(err);
        }
    }

    @Action(DeleteAccessRight)
    public async deleteAccessRight(
        ctx: StateContext<EnedisStateModel>,
        action: DeleteAccessRight,
    ): Promise<void> {
        try {
            const msg = await this.enedisService.deleteEnedisAccessRight(
                action.payload.accessRightId,
            );

            ctx.dispatch(GetAllEnedisAccessRights);
        } catch (err: any) {
            console.log(err);
        }
    }

    @Action(RetryAccessRight)
    public async retryAccessRight(
        ctx: StateContext<EnedisStateModel>,
        action: RetryAccessRight,
    ): Promise<void> {
        try {
            const msg = await this.enedisService.retryEnedisAccessRight(
                action.payload.accessRightId,
            );
        } catch (err: any) {
            console.log(err);
            throw err;
        } finally {
            ctx.dispatch(GetAllEnedisAccessRights);
        }
    }
}
